<template>
  <div class="geolocation-map-preview">
    <div
      ref="mapContainer"
      class="geolocation-map-preview__map"
      :class="{ 'is-visible': locations.length > 0 }"
      :aria-label="t('leaflets.products.geolocation.map.ariaLabel')"
    />
    <div v-if="locations.length === 0" class="geolocation-map-preview__empty">
      {{ t('leaflets.products.geolocation.map.empty') }}
    </div>
    <div
      v-if="showAvailabilityLegend && locations.length > 0"
      class="geolocation-map-preview__legend"
      :aria-label="t('leaflets.products.geolocation.map.legend.ariaLabel')"
    >
      <span>
        <i class="geolocation-map-preview__legend-marker is-available" />
        {{ t('leaflets.products.geolocation.map.legend.available') }}
      </span>
      <span>
        <i class="geolocation-map-preview__legend-marker is-unavailable" />
        {{ t('leaflets.products.geolocation.map.legend.unavailable') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  circle,
  circleMarker,
  type LatLngBounds,
  latLngBounds,
  type Layer,
  type Map as LeafletMap,
  map as createMap,
  tileLayer
} from 'leaflet'

import type { GeolocationApiModel } from '@/types/GeolocationModel'

import 'leaflet/dist/leaflet.css'

const props = defineProps<{
  locations: GeolocationApiModel[]
  showAvailabilityLegend?: boolean
}>()

const { t } = useI18n()
const mapContainer = ref<HTMLElement>()

let leafletMap: LeafletMap | null = null
let locationLayers: Layer[] = []
let currentBounds: LatLngBounds | null = null
let resizeObserver: ResizeObserver | null = null

function initializeMap(initialLocation: GeolocationApiModel): void {
  if (leafletMap || !mapContainer.value) return

  leafletMap = createMap(mapContainer.value, {
    zoomControl: true,
    attributionControl: true
  })
  leafletMap.setView([initialLocation.lat, initialLocation.lng], 6)

  tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(leafletMap)
}

function clearLocationLayers(): void {
  if (!leafletMap) return

  locationLayers.forEach(layer => leafletMap?.removeLayer(layer))
  locationLayers = []
}

async function renderLocations(): Promise<void> {
  await nextTick()

  if (!props.locations.length) {
    clearLocationLayers()
    currentBounds = null
    return
  }

  initializeMap(props.locations[0])
  if (!leafletMap) return

  clearLocationLayers()

  const bounds = latLngBounds([])

  props.locations.forEach(location => {
    const color = location.availabilityType === 'UNAVAILABLE' ? '#f56c6c' : '#409eff'
    const area = circle([location.lat, location.lng], {
      radius: location.radius,
      color,
      fillColor: color,
      fillOpacity: 0.16,
      weight: 2
    }).addTo(leafletMap!)
    const center = circleMarker([location.lat, location.lng], {
      radius: 4,
      color: '#ffffff',
      fillColor: color,
      fillOpacity: 1,
      weight: 2
    }).addTo(leafletMap!)

    locationLayers.push(area, center)
    bounds.extend(area.getBounds())
  })

  currentBounds = bounds
  leafletMap.invalidateSize()
  leafletMap.fitBounds(currentBounds, { padding: [20, 20], maxZoom: 15 })
}

onMounted(() => {
  resizeObserver = new ResizeObserver(() => {
    if (!leafletMap) return

    leafletMap.invalidateSize()
    if (currentBounds) {
      leafletMap.fitBounds(currentBounds, { padding: [20, 20], maxZoom: 15 })
    }
  })
  if (mapContainer.value) resizeObserver.observe(mapContainer.value)
  void renderLocations()
})

watch(
  () => props.locations,
  () => void renderLocations(),
  { deep: true }
)

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  clearLocationLayers()
  leafletMap?.remove()
  leafletMap = null
})
</script>

<style scoped lang="scss">
.geolocation-map-preview {
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  border: 1px solid var(--el-border-color);
  border-radius: var(--el-border-radius-base);
  background: var(--el-fill-color-light);

  &__map {
    width: 100%;
    height: 100%;
    visibility: hidden;

    &.is-visible {
      visibility: visible;
    }
  }

  &__empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: var(--el-text-color-secondary);
    text-align: center;
  }

  &__legend {
    position: absolute;
    right: 10px;
    bottom: 10px;
    z-index: 500;
    display: flex;
    gap: 12px;
    padding: 6px 10px;
    border: 1px solid var(--el-border-color-light);
    border-radius: var(--el-border-radius-base);
    background: rgb(255 255 255 / 92%);
    color: var(--el-text-color-regular);
    font-size: 12px;

    span {
      display: inline-flex;
      gap: 5px;
      align-items: center;
    }
  }

  &__legend-marker {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #409eff;

    &.is-unavailable {
      background: #f56c6c;
    }
  }
}

@media (min-width: 1200px) {
  .geolocation-map-preview {
    height: 320px;
  }
}
</style>
